import {useEffect, useRef, useState} from 'react';
import {useQuery} from '@apollo/client';
import {DownOutlined, UpOutlined} from '@ant-design/icons';
import moment from 'moment';
import {formatMoney} from 'utils/currencyFormatting';
import {cardUsd} from 'pages/wallet/view/swap/swapReporting';
import {GET_WALLET_TRANSACTION_SUMMARY} from 'graphql/queries/wallet';
import shell from '../view/swap/swapTransactions.module.scss';
import styles from './walletTransaction.module.scss';

// Same cards as the swap page, plus Cancelled. `status` is what clicking the
// card filters the list to; All clears it. Pending also counts CREATED rows,
// which the list cannot filter by, so its card filters to PENDING.
const CARDS = [
  {key: 'all', status: undefined, tone: shell.toneAll, label: 'All'},
  {
    key: 'pending',
    status: 'PENDING',
    tone: shell.tonePending,
    label: 'Pending',
  },
  {
    key: 'successful',
    status: 'SUCCESSFUL',
    tone: shell.toneSuccessful,
    label: 'Successful',
  },
  {key: 'failed', status: 'FAILED', tone: shell.toneFailed, label: 'Failed'},
  {
    key: 'cancelled',
    status: 'CANCELLED',
    tone: styles.toneCancelled,
    label: 'Cancelled',
  },
];

const IGNORED_FILTERS = ['status', 'statuses', 'export', 'adminEmail'];

/**
 * The list's input, minus what the summary ignores.
 *
 * The backend already ignores status, pagination and export on this query.
 * Dropping them here as well keeps the variables stable, so turning a page or
 * picking a status doesn't refetch five totals that cannot have changed.
 */
export const summaryInput = (listInput = {}) => {
  const filters = {...(listInput.filters || {})};
  IGNORED_FILTERS.forEach(key => delete filters[key]);
  const input = {...listInput, filters};
  delete input.pagination;
  return input;
};

const plural = count =>
  `${count.toLocaleString('en-US')} transaction${count === 1 ? '' : 's'}`;

const Row = ({label, value, muted}) => (
  <span className={shell.cardTotalRow}>
    <span className={shell.cardTotalCode}>{label}</span>
    <span
      className={`${shell.cardTotal} ${muted ? styles.summaryMuted : ''}`}
      title={value}>
      {value}
    </span>
  </span>
);

/**
 * Count and totals per status for exactly the rows the list pages through,
 * built like the swap page's cards: each card is also a status tab, and the
 * cards ignore the status filter, so all five stay filled while one is picked.
 *
 * `listInput` is the list query's live input, so every filter the list applies
 * reaches the summary without being restated here. `refreshSignal` changes
 * after a mutation that can move a transaction between statuses — the filters
 * are the same then, so nothing else would make these totals refetch.
 */
const TransactionSummaryCards = ({
  listInput,
  refreshSignal,
  activeStatus,
  onSelect,
}) => {
  const [expanded, setExpanded] = useState(true);
  const input = summaryInput(listInput);
  const {data, loading, error, refetch} = useQuery(
    GET_WALLET_TRANSACTION_SUMMARY,
    {
      variables: {input},
      skip: !input.currency,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    },
  );

  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    // Only the signal should trigger this; filter changes refetch on their own.
    refetch();
  }, [refreshSignal]);

  const result = data?.getWalletTransactionSummary;
  const summary =
    result?.__typename === 'WalletTransactionSummary' ? result : null;
  const errorMessage =
    result?.__typename === 'Error'
      ? result.message
      : error && !result
      ? "Couldn't load the transaction summary."
      : null;
  const currency = summary?.currency || input.currency;

  return (
    <section className={styles.summary}>
      <div className={styles.summaryHead}>
        {/* The list has no default range but the summary does (the 366 days
            before the end date), so say which days these totals cover. */}
        <span className={styles.summaryRange}>
          {summary?.startDate && summary?.endDate
            ? `${moment.utc(summary.startDate).format('DD MMM YYYY')} – ${moment
                .utc(summary.endDate)
                .format('DD MMM YYYY')} · USD at each day's rate (UTC)`
            : ' '}
        </span>
        <button
          type="button"
          className={shell.summaryToggle}
          aria-expanded={expanded}
          onClick={() => setExpanded(open => !open)}>
          {expanded ? 'Hide breakdown' : 'Show breakdown'}
          {expanded ? <UpOutlined /> : <DownOutlined />}
        </button>
      </div>

      <div className={styles.summaryCards}>
        {CARDS.map(card => {
          const totals = summary?.[card.key];
          const isActive = (activeStatus || undefined) === card.status;
          return (
            <button
              type="button"
              key={card.key}
              aria-pressed={isActive}
              className={[
                shell.statusTab,
                card.tone,
                card.key === 'all' && styles.summaryAll,
                isActive && shell.statusTabActive,
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => onSelect?.(card.status)}>
              <span className={shell.statusTabLabel}>{card.label}</span>
              <span
                className={shell.statusTabAmount}
                title={
                  totals ? formatMoney(totals.amount, currency) : undefined
                }>
                {totals
                  ? formatMoney(totals.amount, currency)
                  : loading
                  ? '…'
                  : '–'}
              </span>
              <span className={shell.statusTabCount}>
                {totals ? (
                  <>
                    {plural(totals.count)}
                    <span
                      className={styles.summaryUsd}
                      title={`≈ ${cardUsd(totals.normalizedUsd).full}`}>
                      ≈ {cardUsd(totals.normalizedUsd).short}
                    </span>
                  </>
                ) : (
                  ' '
                )}
              </span>
              {totals && (
                // Always rendered, so opening and closing can animate.
                <span
                  className={`${shell.cardDetailsWrap} ${
                    expanded ? shell.cardDetailsOpen : ''
                  }`}
                  aria-hidden={!expanded}>
                  <span className={shell.cardDetailsInner}>
                    <span className={shell.cardDetails}>
                      {totals.count > 0 ? (
                        <span className={shell.cardTotals}>
                          <Row
                            label="Net"
                            value={formatMoney(totals.net, currency)}
                          />
                          <Row
                            label="Fees"
                            value={formatMoney(totals.fees, currency)}
                            muted
                          />
                        </span>
                      ) : (
                        <span className={shell.cardEmpty}>No transactions</span>
                      )}
                    </span>
                  </span>
                </span>
              )}
              {!totals && errorMessage && card.key === 'all' && (
                <span className={shell.cardError}>{errorMessage}</span>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default TransactionSummaryCards;
